import delegate from "delegate-it";
import scrollReveal from "@/scripts/components/scroll-reveal";
import ajax from "@/scripts/common/ajax";

const pageContent = document.querySelector("[data-page-content]");
let activeController = null;
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const donutRadius = 90;
const donutCircumference = 2 * Math.PI * donutRadius;

const handleFetchPage = async (e) => {
  e.preventDefault();

  activeController?.abort();

  const controller = new AbortController();
  activeController = controller;

  const loadingTimer = setTimeout(() => {
    pageContent.classList.add("is-loading");
  }, 300);

  const url = e.delegateTarget.href;

  try {
    const res = await ajax.get(url, { signal: controller.signal }).text();

    pageContent.innerHTML = res;

    requestAnimationFrame(() => {
      pageContent.querySelectorAll("[data-page-elements] [data-sr-group]").forEach((group) => {
        scrollReveal.reveal(group);
      });
    });

    window.history.replaceState({}, "", url);
  } catch (err) {
    if (err.name === "AbortError") return;
    throw err;
  } finally {
    clearTimeout(loadingTimer);

    if (activeController === controller) {
      pageContent.classList.remove("is-loading");
      activeController = null;
    }
  }
};

const animateVeiGraphic = (wrap) => {
  const fill = wrap.querySelector(".donut-fill");
  const label = wrap.querySelector(".donut-label");
  if (!fill || !label) return;

  const percent = parseFloat(wrap.dataset.veiGraphic);
  if (!Number.isFinite(percent)) return;

  wrap.dataset.veiGraphicAnimated = "1";

  const finalDash = `${(percent / 100) * donutCircumference} ${donutCircumference}`;
  const finalLabel = `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;

  if (reducedMotion.matches) {
    fill.style.strokeDasharray = finalDash;
    label.textContent = finalLabel;
    return;
  }

  requestAnimationFrame(() => {
    fill.style.strokeDasharray = finalDash;
  });

  const start = performance.now();
  const dur = 1200;
  const tick = (t) => {
    const p = Math.min((t - start) / dur, 1);
    const current = p * percent;
    const formatted = Number.isInteger(percent) ? Math.round(current) : current.toFixed(1);
    label.textContent = formatted + "%";
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

const maybeAnimateVeiGraphic = (el) => {
  if (el.nodeType !== 1 || !el.matches("[data-vei-graphic]")) return;
  if (el.dataset.veiGraphicAnimated) return;
  if (!reducedMotion.matches && el.closest("[data-sr-group]") && !el.hasAttribute("data-sr-touched")) return;

  animateVeiGraphic(el);
};

const scanVeiGraphics = (root = document) => {
  if (root.nodeType !== 1 && root.nodeType !== 9) return;

  if (root.matches?.("[data-vei-graphic]")) {
    maybeAnimateVeiGraphic(root);
  }

  root.querySelectorAll?.("[data-vei-graphic]").forEach(maybeAnimateVeiGraphic);
};

const observeVeiGraphics = () => {
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes") {
        maybeAnimateVeiGraphic(m.target);
        continue;
      }

      m.addedNodes.forEach(scanVeiGraphics);
    }
  }).observe(document.body, {
    attributes: true,
    attributeFilter: ["data-sr-touched"],
    childList: true,
    subtree: true,
  });

  scanVeiGraphics();
};

const init = () => {
  delegate("[data-volume-switcher-page]", "click", handleFetchPage);
  observeVeiGraphics();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
